import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
} from "@mui/material";
import Layout from "../../components/Layout";
import { useAppContext } from "../../context/AppContext";
// import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useLogoSrc } from "../../hooks/useLogoSrc";
import ApiService from "../../services/api";
import { isTokenExpired } from "../../utils/tokenUtils";
import { cookieHelpers } from "../../utils/cookieUtils";


const DistributorCodeHandler: React.FC = () => {
//   const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSnackbar, setToken, setRole, setDistributorId, getTranslation, token, translationData, loadTranslations, setEnableSessionToCreateNew, clearAppState } = useAppContext();
  const logoSrc = useLogoSrc();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [translationsReady, setTranslationsReady] = useState(false);

  // Load translations on mount using centralized function
  // This is needed because SSO redirect lands here directly, bypassing LanguageSelector
  useEffect(() => {
    const initializeTranslations = async () => {
      // If translations already exist, proceed immediately
      if (translationData) {
        setTranslationsReady(true);
        return;
      }

      // Use centralized loadTranslations function
      await loadTranslations();
      setTranslationsReady(true);
    };

    initializeTranslations();
  }, [translationData, loadTranslations]);

  useEffect(() => {
    // Wait for translations to be ready before processing code
    if (!translationsReady) {
      return;
    }

    const handleCodeProcessing = async () => {
      // Check if user already has a valid distributor token
      const storedToken = token || cookieHelpers.getToken('distributor');
      if (storedToken && !isTokenExpired(storedToken)) {
        // User already authenticated, redirect to successful page
        navigate('/distributor', { replace: false });
        return;
      }

      const code = searchParams.get('code');

      if (!code) {
        setErrorMessage('No authorization code found in URL.');
        setHasError(true);
        setIsLoading(false);
        setSnackbar({
          message: getTranslation("Err91"),
          open: true,
          severity: "error",
        });
        return;
      }

      const codeAndState = `${code}`;
      
      try {
        const apiService = new ApiService();
        const result = await apiService.distributorSignIn(codeAndState);
        
        if (result.token) {
          setToken(result.token);
          // Context setToken already stores in cookies
        }
        if (result.distributor_id) {
          setDistributorId(result.distributor_id);
          setEnableSessionToCreateNew(true);
        }
        // Set distributor role
        setRole(1);
    
        // Store a flag to indicate successful login
        sessionStorage.setItem('ssoLoginSuccess', 'true');

        // Use navigate to create a new history entry (replace: false)
        // This keeps navigation within SPA and creates history entry
        navigate('/distributor', { replace: false });
      } catch (error) {
        console.error('Error processing authorization code:', error);
        const isNetworkError =
          typeof error === 'object' &&
          error !== null &&
          ((error as { isNetworkError?: boolean }).isNetworkError === true ||
            (error as { errorType?: string }).errorType === 'NETWORK_ERROR');
        const displayMessage = isNetworkError
          ? getTranslation(
              'Txt1017',
              'Network Error: No response received from server. Please check your internet connection or try again later.'
            )
          : error instanceof Error
            ? error.message
            : getTranslation('Err92', 'Failed to process Herbalife authentication');
        setErrorMessage(displayMessage);
        setHasError(true);
        setIsLoading(false);
        setSnackbar({
          message: isNetworkError ? displayMessage : getTranslation('Err92'),
          open: true,
          severity: 'error',
        });
      }
    };

    handleCodeProcessing();
  }, [searchParams, setToken, setDistributorId, setSnackbar, token, getTranslation, navigate, translationsReady, setRole, setEnableSessionToCreateNew]);

  const redirectToDistributorLogin = useCallback(() => {
    sessionStorage.clear();
    clearAppState();
    navigate('/distributor', { replace: true });
  }, [clearAppState, navigate]);

  return (
    <Layout showBackButton={false}>
      <Box
        sx={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          backgroundColor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          padding: '20px',
          boxSizing: 'border-box',
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt="Company Logo"
          sx={{ 
            height: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
            width: "100%", 
            objectFit: "contain" 
          }}
        />
        
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {isLoading && !hasError && (
            <>
              <CircularProgress 
                size={60}
                sx={{ 
                  color: "var(--color-text-primary)",
                  mb: 2
                }} 
              />
              
              <Typography
                sx={{
                  color: "var(--color-text-primary)",
                  textAlign: "center",
                  fontSize: "1.2rem",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "normal",
                  mb: 1,
                }}
              >
               {getTranslation("Txt899" )}
              </Typography>
              
              <Typography
                sx={{
                  color: "var(--color-text-secondary)",
                  textAlign: "center",
                  fontSize: "0.9rem",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "normal",
                  maxWidth: "300px",
                }}
              >
               {getTranslation("Txt900" )}
              </Typography>
            </>
          )}

          {hasError && (
            <>
              <Typography
                sx={{
                  color: "#FF2B1F",
                  textAlign: "center",
                  fontSize: "1.2rem",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "normal",
                  mb: 1,
                }}
              >
                {getTranslation("Err84" )}
              </Typography>
              
              <Typography
                sx={{
                  color: "var(--color-text-secondary)",
                  textAlign: "center",
                  fontSize: "0.9rem",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "normal",
                  maxWidth: "400px",
                  mb: 2,
                }}
              >
                {errorMessage}
              </Typography>

              <Button
                variant="contained"
                onClick={redirectToDistributorLogin}
                sx={{
                  backgroundColor: "var(--color-primary)",
                  color: "#FFFFFF",
                  padding: "12px 24px",
                  fontSize: "1rem",
                  fontWeight: 500,
                  textTransform: "none",
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "#333333",
                  },
                }}
              >
               {getTranslation("Txt901" )}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default DistributorCodeHandler;
